import IAppLogger from '@app/lib/IAppLogger';
import {
  AuthorizationNotifier,
  AuthorizationRequest,
  AuthorizationRequestHandler,
  AuthorizationServiceConfiguration,
  BaseTokenRequestHandler,
  BasicQueryStringUtils,
  DefaultCrypto,
  FetchRequestor,
  GRANT_TYPE_AUTHORIZATION_CODE,
  GRANT_TYPE_REFRESH_TOKEN,
  LocalStorageBackend,
  LocationLike,
  RedirectRequestHandler,
  StringMap,
  TokenRequest,
  TokenRequestHandler,
  TokenResponse,
} from '@openid/appauth';

type AuthorizationCodeResponse = {
  code: string;
  codeVerifier?: string;
};

class NoHashQueryStringUtils extends BasicQueryStringUtils {
  parse(input: LocationLike) {
    return super.parse(input, false);
  }
}

const AUTH_STATE_KEY = 'auth_response';

export class AuthFlow {
  private issuerUrl: string;
  private clientId: string;
  private scopes: string[];
  private loginRedirectUri: string;
  private logoutRedirectUri: string;
  private logger: IAppLogger;
  private notifier: AuthorizationNotifier;
  private requestor = new FetchRequestor();
  private authorizationHandler: AuthorizationRequestHandler;
  private tokenHandler: TokenRequestHandler;
  private configuration: AuthorizationServiceConfiguration | undefined;
  private accessTokenResponse: TokenResponse | undefined;
  private authorizationCodeResponse: AuthorizationCodeResponse | undefined;
  private onStateChanged: (authenticated: boolean) => void;

  constructor(
    issuerUrl: string,
    clientId: string,
    scopes: string[],
    loginRedirectPath: string,
    logoutRedirectPath: string,
    logger: IAppLogger,
    onStateChanged: (authenticated: boolean) => void,
  ) {
    this.issuerUrl = issuerUrl;
    this.clientId = clientId;
    this.scopes = scopes;
    this.loginRedirectUri = this.createRedirectUri(loginRedirectPath);
    this.logoutRedirectUri = this.createRedirectUri(logoutRedirectPath);
    this.logger = logger;
    this.onStateChanged = onStateChanged;
    this.notifier = new AuthorizationNotifier();
    this.authorizationHandler = new RedirectRequestHandler(
      new LocalStorageBackend(),
      new NoHashQueryStringUtils(),
      window.location,
      new DefaultCrypto(),
    );
    this.tokenHandler = new BaseTokenRequestHandler(this.requestor);
    this.authorizationHandler.setAuthorizationNotifier(this.notifier);
    this.notifier.setAuthorizationListener((request, response, error) => {
      this.logger.debug(
        'authorization request complete ',
        request,
        response,
        error,
      );
      if (response) {
        this.authorizationCodeResponse = {
          code: response.code,
          codeVerifier: request.internal?.code_verifier,
        };
      }
    });
    this.readState();
  }

  public get accessToken(): string | undefined {
    return this.accessTokenResponse?.accessToken;
  }

  public async fetchServiceConfiguration(): Promise<void> {
    const response = await AuthorizationServiceConfiguration.fetchFromIssuer(
      this.issuerUrl,
      this.requestor,
    );
    this.logger.debug('fetched service configuration', response);
    this.configuration = response;
  }

  makeAuthorizationRequest(username?: string, idp?: string): void {
    if (!this.configuration) {
      this.logger.error('unknown service configuration');
      throw new Error('Unknown service configuration');
    }

    const extras: StringMap = { access_type: 'offline' };
    if (username) {
      extras['login_hint'] = username;
    }
    if (idp) {
      extras['idp'] = idp;
    }

    // create a request
    const request = new AuthorizationRequest({
      client_id: this.clientId,
      redirect_uri: this.loginRedirectUri,
      scope: this.createScope(this.scopes),
      response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
      state: undefined,
      extras: extras,
    });

    this.logger.debug(
      'making authorization request ',
      this.configuration,
      request,
    );

    this.authorizationHandler.performAuthorizationRequest(
      this.configuration,
      request,
    );
  }

  public async completeAuthorization(): Promise<void> {
    await this.authorizationHandler.completeAuthorizationRequestIfPossible();
    if (this.authorizationCodeResponse) {
      await this.makeRefreshTokenRequest(
        this.authorizationCodeResponse.code,
        this.authorizationCodeResponse.codeVerifier,
      );
      await this.refreshTokens();
      this.logger.debug('complete');
    }
  }

  private async makeRefreshTokenRequest(
    code: string,
    codeVerifier: string | undefined,
  ): Promise<TokenResponse | undefined> {
    if (!this.configuration) {
      this.logger.error('unknown service configuration');
      return Promise.resolve(undefined);
    }

    const extras: StringMap = {};

    if (codeVerifier) {
      extras.code_verifier = codeVerifier;
    }

    // use the code to make the token request.
    const request = new TokenRequest({
      client_id: this.clientId,
      redirect_uri: this.loginRedirectUri,
      grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
      code: code,
      refresh_token: undefined,
      extras: extras,
    });

    const response = await this.tokenHandler.performTokenRequest(
      this.configuration,
      request,
    );
    this.accessTokenResponse = response;
    this.writeState();
    return response;
  }

  public signedIn(): boolean {
    return (
      !!this.accessTokenResponse &&
      !!this.accessTokenResponse.accessToken &&
      !!this.accessTokenResponse.refreshToken
    );
  }

  public isTokenValid(buffer: number): boolean {
    if (
      !!this.accessTokenResponse &&
      !!this.accessTokenResponse.accessToken &&
      !!this.accessTokenResponse.refreshToken
    ) {
      return this.accessTokenResponse.isValid(buffer);
    }
    return false;
  }

  public makeEndSessionRequest() {
    const location = `${this.issuerUrl}/connect/endsession?post_logout_redirect_uri=${encodeURIComponent(this.logoutRedirectUri)}&id_token_hint=${this.accessTokenResponse?.idToken}`;

    // forget all cached token state
    this.accessTokenResponse = undefined;
    this.writeState();

    // redirect to the end-session endpoint
    window.location.href = location;
  }

  public clear() {
    // forget all cached token state
    this.accessTokenResponse = undefined;
    this.writeState();
  }

  public async refreshTokens(): Promise<string> {
    if (!this.configuration) {
      this.logger.error('unknown service configuration');
      return Promise.reject('Unknown service configuration');
    }
    if (!this.accessTokenResponse?.refreshToken) {
      this.logger.warn('Missing refreshToken.');
      return Promise.resolve('Missing refreshToken.');
    }
    if (this.accessTokenResponse && this.accessTokenResponse.isValid()) {
      // do nothing
      return Promise.resolve(this.accessTokenResponse.accessToken);
    }
    const request = new TokenRequest({
      client_id: this.clientId,
      redirect_uri: this.loginRedirectUri,
      grant_type: GRANT_TYPE_REFRESH_TOKEN,
      code: undefined,
      refresh_token: this.accessTokenResponse.refreshToken,
      extras: undefined,
    });

    const response = await this.tokenHandler.performTokenRequest(
      this.configuration,
      request,
    );
    this.accessTokenResponse = response;
    this.writeState();
    return response.accessToken;
  }

  private createScope(scope: string[]): string {
    const allScopes = [...scope];

    if (!allScopes.includes('offline_access')) {
      allScopes.push('offline_access');
    }

    if (!allScopes.includes('profile')) {
      allScopes.push('profile');
    }

    return allScopes.join(' ');
  }

  private createRedirectUri(redirectPath: string): string {
    return `${window.location.origin}${redirectPath}`;
  }

  private writeState(): void {
    // this is temporary
    const prevState = localStorage.getItem(AUTH_STATE_KEY);
    const newState = this.accessTokenResponse
      ? JSON.stringify(this.accessTokenResponse.toJson())
      : '';
    localStorage.setItem(AUTH_STATE_KEY, newState);
    const hasPrevState = prevState !== null && prevState !== '';
    const hasNewState = newState !== null && newState !== '';
    if (hasPrevState !== hasNewState) {
      this.onStateChanged(!!this.accessTokenResponse);
    }
  }

  private readState(): void {
    const state = localStorage.getItem(AUTH_STATE_KEY);
    if (state === '' || state === null) {
      this.accessTokenResponse = undefined;
    } else {
      this.accessTokenResponse = new TokenResponse(JSON.parse(state));
    }
  }
}
